/**
 * How a screen configures the Focus bar. One config per screen, defined once
 * next to the screen's URL schema and passed to `useFocus`:
 *
 *   const CONTROLS = { allApps: false, dates: true, filters: { status: false, thresholds: false, activity: false } } as const;
 *   export const parseInsightsSearch = (raw: RawSearch) => ({ ...parseFocusSearch(raw, CONTROLS), metric: param.string(raw, 'metric') });
 *   export const INSIGHTS_FOCUS = defineFocusConfig({ ...CONTROLS, parse: parseInsightsSearch, appSwitcher: { … } });
 *
 * The controls decide both what the bar shows AND which scope keys the URL
 * keeps: a key for a control the screen does not offer is dropped on read.
 */
import type { FocusSearch } from './search';
import type { RawSearch } from '@/router/search';

/** Which refinements the Filters panel offers. Apps, networks, accounts,
 * campaign/ad group/ad, asset types, country, placement and OS are always
 * part of it; these three are screen-specific. */
export interface FilterControls {
  /** Hide when a screen owns a dedicated creative-type selector. */
  assetTypes?: boolean;
  /** The asset Status field (`status`). Performance only: it is a table filter. */
  status: boolean;
  /** Minimum impressions / clicks / installs / spend (`min_*`). */
  thresholds: boolean;
  /** "Impressions in the last 24 hours" (`active_during_past_n_hours`). */
  activity: boolean;
}

export interface FocusControls {
  /** Offer "All apps" (the portfolio, `all_apps=1`). Performance only (decision record §2). */
  allApps: boolean;
  /** Show the date chip, and resolve missing dates into the URL. */
  dates: boolean;
  /** Show the Filters chip and the active-filter chips; `false` hides both
   * and the screen reads no filter keys. */
  filters: FilterControls | false;
}

export interface FocusConfig<S extends FocusSearch = FocusSearch> extends FocusControls {
  /**
   * The screen's WHOLE URL schema: `parseFocusSearch(raw, controls)` spread
   * with the screen's own keys. The bar re-parses every URL it writes with
   * it, so scope changes keep the screen's keys and every URL is canonical.
   * It is also the route's `validateSearch` (`defineSearch(parse)`).
   */
  parse: (raw: RawSearch) => S;
  /**
   * Adjust the screen's own keys when the user changes the scope (apps,
   * dates, filters), e.g. go back to page 1. Not called while the bar
   * completes a URL (Focus resolution), which never changes what was asked.
   */
  onScopeChange?: (next: S) => S;
  /** Copy in the app switcher. */
  appSwitcher: {
    /** What the evidence pills are for: "statuses and insights". */
    evidenceFor: string;
    /** The line under the list, e.g. "Click an app to look at it alone, or tick several to combine them." */
    hint: string;
    /** Without All apps: say that the portfolio is on Performance, as a link. */
    portfolioOnPerformance?: boolean;
  };
}

/**
 * Declare a screen's Focus config. Keeps `dates` as a literal type, so
 * `useFocus` knows whether the resolved scope carries dates.
 */
export const defineFocusConfig = <S extends FocusSearch, const D extends boolean>(config: FocusConfig<S> & { dates: D }): FocusConfig<S> & { dates: D } =>
  config;
